import {
  FEEDBACK_QUESTION_LABELS,
  FEEDBACK_QUESTION_OPTIONS,
} from "../../libs/workout/constants";
import { FEEDBACK_QUESTION_IDS, Workout } from "../../libs/workout/types";
import { calculateFeedbackOutcomeInt } from "../../libs/workout/utils";

type FeedbackTableProps = {
  workout: Workout;
  weeksColumns: string[];
};
export const FeedbackTable: React.FC<FeedbackTableProps> = ({
  workout,
  weeksColumns,
}) => {
  return (
    <div className="bg-gray-900 dark:bg-gray-800 dark:border dark:border-gray-700 rounded-2xl p-6 mt-8">
      <h2 className="text-lg font-bold text-gray-100 mb-6">Feedback</h2>

      <div className="overflow-x-auto sm:rounded-lg">
        <table className="w-full text-sm text-gray-300 text-center">
          <thead>
            <tr className="text-xs text-gray-400 uppercase bg-gray-800/50">
              <th className="px-5 py-3 text-left">Domanda</th>
              {weeksColumns.map((column, i) => (
                <th
                  key={column + i}
                  className="px-4 py-3 text-center"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(FEEDBACK_QUESTION_IDS).map((feedbackQuestionId, i) => (
              <tr
                key={feedbackQuestionId + "-" + i}
                className="border-b border-gray-800 hover:bg-gray-800/50"
              >
                <td className="px-5 py-2.5 text-left font-medium text-gray-200">
                  {
                    FEEDBACK_QUESTION_LABELS[
                      feedbackQuestionId as keyof typeof FEEDBACK_QUESTION_IDS
                    ]
                  }
                </td>
                {weeksColumns.map((_column, index) => (
                  <td key={index} className="px-4 py-2.5 text-center">
                    {getSetWeekAmountForFeedbackQuestionId(
                      workout,
                      feedbackQuestionId as keyof typeof FEEDBACK_QUESTION_IDS,
                      index
                    )}
                  </td>
                ))}
              </tr>
            ))}
            {/* Riga Totale */}
            <tr className="border-t-2 border-gray-600 bg-gray-800/80 font-bold">
              <td className="px-5 py-3 text-left text-gray-100">
                Punteggio Totale
              </td>
              {weeksColumns.map((_column, index) => (
                <td key={index} className="px-4 py-3 text-center">
                  {getFeedbackTotalScore(workout, index)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const getSetWeekAmountForFeedbackQuestionId = (
  workout: Workout,
  feedbackQuestionId: keyof typeof FEEDBACK_QUESTION_IDS,
  weekIndex: number
) => {
  if (
    !workout.data[weekIndex].feedbackForm ||
    !workout.data[weekIndex].feedbackForm?.questions
  ) {
    return "—";
  }

  const question = workout.data[weekIndex].feedbackForm?.questions.find(
    (question) => {
      return question.id === feedbackQuestionId;
    }
  );

  if (!question || !question.value) {
    return "—";
  }

  return (
    <span>
      {FEEDBACK_QUESTION_OPTIONS[feedbackQuestionId][parseInt(question?.value)]}
    </span>
  );
};

const getFeedbackTotalScore = (workout: Workout, weekIndex: number) => {
  if (
    !workout.data[weekIndex].feedbackForm ||
    !workout.data[weekIndex].feedbackForm?.questions
  ) {
    return "—";
  }

  const questions = workout.data[weekIndex].feedbackForm?.questions;
  if (!questions || questions.length === 0) {
    return "—";
  }

  const totalScore = calculateFeedbackOutcomeInt(questions);
  const maxScore = questions.length * 3;

  let status = { color: "", message: "", icon: "" };

  if (totalScore >= 20) {
    status = {
      color: "text-green-400",
      message: "Recupero ottimo",
      icon: "🎉",
    };
  } else if (totalScore >= 15) {
    status = {
      color: "text-blue-400",
      message: "Recupero buono",
      icon: "😊",
    };
  } else if (totalScore >= 11) {
    status = {
      color: "text-yellow-400",
      message: "Recupero scarso",
      icon: "⚠️",
    };
  } else {
    status = {
      color: "text-red-400",
      message: "Sovraccarico",
      icon: "🚨",
    };
  }

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex items-center gap-1.5">
        <span className="text-sm">{status.icon}</span>
        <span className="text-sm font-bold text-gray-100">
          {totalScore} / {maxScore}
        </span>
      </div>
      <span className={`text-[10px] font-semibold ${status.color}`}>
        {status.message}
      </span>
    </div>
  );
};
